import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { documentKeys } from '~/hooks/queries/use-documents';
import { catalogueKeys } from '~/hooks/queries/use-knowledge-catalogue';
import { knowledgeEnrichmentKeys } from '~/hooks/queries/use-knowledge-enrichment';
import { feedKeys, workFileKeys, workBoardKeys, filesOverviewKeys, attentionKeys } from '~/lib/query-keys';

export interface CreateDocumentInput {
  title: string;
  content: string;
  tags?: string;
  summary?: string;
  /** The chat session this save happened in, if any — lets the backend file
   *  the new document on the chat's scoped Work container automatically
   *  (FRONT-1737). Omit for a save with no chat context. */
  sessionId?: string;
}

/** The Work container a save was filed on, or null when the chat had no
 *  scope (or nothing was attached for any other reason) — FRONT-1737. */
export interface AttachedToContainer {
  entityId: string;
  name: string;
  kind: string;
}

export interface CreatedDocument {
  filename: string;
  title: string;
  summary: string;
  tags: string[];
  date: string;
  attachedTo: AttachedToContainer | null;
}

/**
 * Create a brand-new document from arbitrary text (the inbox chat's per-response
 * "Save as document"). The backend mints a unique filename so each save is its
 * own document; on success we refresh both the Files list and the inbox feed
 * (a fresh document surfaces in both).
 *
 * FRONT-1737: when the save came out of a chat scoped to a Work container,
 * the backend files it there automatically and reports it back as
 * `attachedTo` — invalidate that container's own attachment list AND the
 * board's `workBoardKeys.all` (the card's `· N files` chip), same two keys
 * `useAttachFile` invalidates for a manual attach (see
 * use-work-file-mutations.ts's own docblock for why the board key is
 * load-bearing), or the project page and board card go stale.
 */
export function useCreateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateDocumentInput) =>
      api.post<CreatedDocument>('/api/documents', input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      // FRONT-2550: the Smart tagging card's totals count these rows and its status query is cached for 60s, so any change to the set of files must refresh it.
      queryClient.invalidateQueries({ queryKey: knowledgeEnrichmentKeys.all });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-1605 review, finding M2 — see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
      if (data.attachedTo) {
        queryClient.invalidateQueries({ queryKey: workFileKeys.byContainer(data.attachedTo.entityId) });
        queryClient.invalidateQueries({ queryKey: workBoardKeys.all });
      }
    },
  });
}

export interface UpdateDocumentInput {
  filename: string;
  content: string;
  title?: string;
  summary?: string;
  tags?: string;
  /** FRONT-2600: ISO version this save is checked against; the server answers 409 when the row has moved past it. */
  baseUpdatedAt?: string;
}

/**
 * Update an existing document's content in place (the inbox chat's "Update
 * document"). The rewritten content is passed directly — no agent tool — so
 * large documents can't truncate the way a tool-call JSON arg would.
 */
export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ filename, ...body }: UpdateDocumentInput) =>
      api.put<{ filename: string; title: string }>(
        `/api/documents/${encodeURIComponent(filename)}`,
        body,
      ),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: documentKeys.detail(vars.filename) });
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
      queryClient.invalidateQueries({ queryKey: attentionKeys.all });
      queryClient.invalidateQueries({ queryKey: feedKeys.all });
      // FRONT-1605 review, finding M2 — see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: catalogueKeys.all });
      // FRONT-2392: see use-files.ts's matching comment.
      queryClient.invalidateQueries({ queryKey: filesOverviewKeys.lists() });
    },
  });
}
